import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@config/firebase';
import { useAuth } from './AuthContext';

const OrganizationContext = createContext(null);

const STORAGE_KEY = 'torneospro:currentOrgId';

/**
 * Contexto de "organización actual" (workspace).
 * - Para super_admin: permite cambiar entre organizaciones (incluida "Sin organización").
 * - Para org_admin: queda fijado en su propia organización (userData.organizationId).
 * - Persiste la selección en localStorage.
 *
 * Valores especiales:
 *   '__all__'   → ver todas las organizaciones (super_admin)
 *   '__none__'  → solo torneos sin organizationId (super_admin)
 *   '<orgId>'   → una organización concreta
 */
export const OrganizationProvider = ({ children }) => {
  const { isAdmin, userData } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [currentOrgId, setCurrentOrgIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || '__all__';
    } catch {
      return '__all__';
    }
  });

  // Cargar organizaciones activas (solo si super_admin las necesita para el switcher)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!isAdmin) {
          // org_admin / referee / viewer: forzar a su propia org
          if (alive) {
            setOrganizations([]);
            setLoaded(true);
          }
          return;
        }
        const snap = await getDocs(query(collection(db, 'organizations'), where('active', '==', true)));
        if (!alive) return;
        const orgs = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setOrganizations(orgs);
        setLoaded(true);
      } catch (err) {
        console.error('Error loading organizations:', err);
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [isAdmin]);

  // Para usuarios no super_admin, el currentOrgId es siempre su propia organización (no editable).
  const effectiveOrgId = isAdmin ? currentOrgId : (userData?.organizationId || '__all__');

  const setCurrentOrgId = (id) => {
    if (!isAdmin) return; // protección: usuarios no admin no pueden cambiarlo
    setCurrentOrgIdState(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch {}
  };

  // Datos derivados
  const currentOrg = effectiveOrgId === '__all__' || effectiveOrgId === '__none__'
    ? null
    : organizations.find(o => o.id === effectiveOrgId) || null;

  const currentOrgLabel =
    effectiveOrgId === '__all__' ? 'Todas las organizaciones'
    : effectiveOrgId === '__none__' ? 'Sin organización'
    : currentOrg?.name || (isAdmin ? 'Sin organización' : '—');

  /**
   * Filtrar tournaments según la organización actual.
   * Devuelve la lista filtrada.
   */
  const filterTournamentsByOrg = (tournaments) => {
    if (!Array.isArray(tournaments)) return [];
    if (effectiveOrgId === '__all__') return tournaments;
    if (effectiveOrgId === '__none__') return tournaments.filter(t => !t.organizationId);
    return tournaments.filter(t => t.organizationId === effectiveOrgId);
  };

  /**
   * Cuando se crea un nuevo torneo, qué organizationId asignarle por defecto.
   * - Si current = orgId concreto → ese.
   * - Si '__all__' o '__none__' → '' (sin org).
   */
  const defaultOrgIdForNew = effectiveOrgId === '__all__' || effectiveOrgId === '__none__'
    ? ''
    : effectiveOrgId;

  const value = useMemo(() => ({
    organizations,
    loaded,
    isAdmin,
    currentOrgId: effectiveOrgId,
    currentOrg,
    currentOrgLabel,
    setCurrentOrgId,
    filterTournamentsByOrg,
    defaultOrgIdForNew,
  }), [organizations, loaded, isAdmin, effectiveOrgId, currentOrg, currentOrgLabel]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const ctx = useContext(OrganizationContext);
  if (!ctx) throw new Error('useOrganization debe usarse dentro de OrganizationProvider');
  return ctx;
};
